import { Component, computed, DestroyRef, effect, inject, viewChild } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { NavigationEnd, Router } from "@angular/router";
import { fromEvent, merge } from "rxjs";
import { filter, finalize } from "rxjs/operators";
import { AlertService, Button, ButtonIcon, Icon, InlineInput, Modal } from "@ui";
import { CorpusDataService } from "../services/corpus-data.service";
import { IngestModalActionComponent } from "./action/action.component";
import { IngestModalFormatComponent } from "./format/format.component";
import { type IngestModalUrlState, IngestModalService } from "./ingest-modal.service";
import { IngestModalRefineComponent } from "./refine/refine.component";
import { IngestModalUploadComponent } from "./upload/upload.component";
import { INGEST_MODAL_PARAM, IngestWindowLocationService } from "./window-location.service";

@Component({
  selector: "app-ingest-modal",
  templateUrl: "./ingest-modal.component.html",
  imports: [
    Modal,
    Button,
    ButtonIcon,
    Icon,
    InlineInput,
    IngestModalUploadComponent,
    IngestModalRefineComponent,
    IngestModalFormatComponent,
    IngestModalActionComponent,
  ],
})
export class IngestModalComponent {
  protected readonly modal = inject(IngestModalService);
  protected readonly refine = viewChild(IngestModalRefineComponent);
  protected readonly action = viewChild(IngestModalActionComponent);
  readonly #alertService = inject(AlertService).withDestroyRef(inject(DestroyRef));
  readonly #corpusService = inject(CorpusDataService);
  readonly #router = inject(Router);
  readonly #location = inject(IngestWindowLocationService);
  // last params written to or read from the URL (serialized), to stop the two syncs echoing
  #syncedKey: string | null = null;

  constructor() {
    // popstate too: after our own pushState the router still thinks it is on the old URL, so it
    // skips Back navigations to it and never emits NavigationEnd
    merge(this.#router.events.pipe(filter((e) => e instanceof NavigationEnd)), fromEvent(window, "popstate"))
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        const params = this.#location.getUrlParams();
        const key: string | null = params[INGEST_MODAL_PARAM] ?? null;
        if (key === this.#syncedKey) {
          return;
        }
        this.#syncedKey = key;
        if (key === null) {
          this.#location.forgetPushedEntry();
          this.modal.close();
        } else if (!this.modal.openFromUrlState(this.#location.paramsToState(params))) {
          // invalid params: put the URL back in line with what the modal shows; while it is still
          // loading (undefined) the effect below corrects the URL once it resolves
          const current = this.modal.urlState();
          if (current !== undefined) {
            this.#syncedKey = this.#serialize(current);
            this.#location.updateBrowserHistory(current);
          }
        }
      });

    effect(() => {
      const state = this.modal.urlState();
      if (state === undefined) {
        return;
      }
      const key = this.#serialize(state);
      if (key === this.#syncedKey) {
        return;
      }
      // one history entry per modal session: opening pushes, step changes replace, so Back closes the modal
      this.#syncedKey = key;
      if (state === null) {
        this.#location.clearBrowserHistory();
      } else {
        this.#location.updateBrowserHistory(state);
      }
    });
  }

  #serialize(state: IngestModalUrlState | null): string | null {
    return state ? this.#location.stateToParams(state)[INGEST_MODAL_PARAM] : null;
  }

  size = computed(() => {
    switch (this.modal.state()) {
      case "upload":
        return "lg";
      default:
        return "full";
    }
  });

  // only the upload state uses the generic header; the other states render their own header markup
  title = computed(() => (this.modal.state() === "upload" ? "Upload data" : ""));

  subtitle = computed(() => (this.modal.state() === "upload" ? "Upload a file to use Pienso features" : ""));

  onCorpusSelected(corpusId: number) {
    this.modal.openRefine(corpusId);
  }

  // user saved a copy of the corpus while refining; stay on the refine step
  onCorpusDerived(corpusId: number) {
    this.modal.openRefine(corpusId);
  }

  // refine step is done; advance to the format step
  onRefined() {
    const corpus = this.modal.corpus();
    if (corpus) {
      this.modal.openFormat(corpus.id);
    }
  }

  onFormatted(pifId: number) {
    if (this.modal.corpus()) {
      this.modal.openAction(pifId);
    }
  }

  isDeriving = false;
  isDownloading = false;

  // Used by the format and action steps; the refine step handles its own copy/download.
  deriveCorpus() {
    const corpus = this.modal.corpus();
    if (!corpus) {
      return;
    }
    this.isDeriving = true;
    this.#corpusService
      .deriveCorpus(corpus.id)
      .pipe(finalize(() => (this.isDeriving = false)))
      .subscribe({
        next: (corpusId: number) => {
          this.#alertService.success("You are now editing a copy of the previous data set.");
          this.modal.openRefine(corpusId);
        },
        error: (e) => this.#alertService.error(e),
      });
  }

  downloadCorpus() {
    const corpus = this.modal.corpus();
    if (!corpus) {
      return;
    }
    this.isDownloading = true;
    this.#corpusService
      .downloadCorpus(corpus)
      .pipe(finalize(() => (this.isDownloading = false)))
      .subscribe({ error: (e) => this.#alertService.error(e) });
  }

  onClosed() {
    this.modal.close();
  }
}
